import { DrawFormData, DrawListFilter, DrawMembersFilter } from '../@types/darw';
import { axiosInstance } from '../utils/axios';

const CREATE_DRAW = '/api/club-owner/draw/';
const DRAW_LIST = '/api/club-owner/draw';
const DRAW_BY_ID = '/public/club-owner/draw/';
const DRAW_PROMO = '/public/club-owner/draw/';
const DRAW_STATS = '/api/club-owner/draw/';
const UPLOAD_IMAGE = '/api/upload/';
const DRAW_MEMBERSHIPS = '/api/club-owner/draw/membership/';
const DELETE_DRAW = '/api/club-owner/draw/';
const UPDATE_DRAW = '/api/club-owner/draw/';
const CREATE_PROMO_DRAW = '/api/club-owner/promo-draw';

const buildQueryString = (filters: Record<string, any>) => {
    return Object.entries(filters)
        .filter(([_, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
};

export const createDraw = async (drawData: DrawFormData) => {
    const payload = {
        name: drawData.name,
        runAt: drawData.runAt,
        timezone: drawData.timezone,
        prize: drawData.prize,
        entryCost: Number(drawData.entryCost),
        runMethod: drawData.runMethod,
        currency: drawData.currency,
        imageId: drawData.imageId,
        discounts: drawData.discounts,
        ticketCap: drawData.ticketCap,
        numberOfEntries: drawData.numberOfEntries || 1,
        // numbers settings are calculated server-side
        termsHtml: drawData.termsHtml,
        emailWinner: drawData.emailWinner,
        promoPeriods: drawData.promoPeriods
    };
    // Include optional drawLinks if provided (tracking links)
    if ((drawData as any).drawLinks && Array.isArray((drawData as any).drawLinks) && (drawData as any).drawLinks.length > 0) {
        (payload as any).drawLinks = (drawData as any).drawLinks;
    }

    try {
        const response = await axiosInstance.post(CREATE_DRAW, payload);
        
        // Return response with any warnings - let calling code handle display
        return response;
    } catch (error) {
        console.error('Error creating draw:', error);
        throw error;
    }
};

export const getDrawsList = (drawFilters: Partial<DrawListFilter>) => {
    const queryString = buildQueryString(drawFilters);
    const endpoint = `${DRAW_LIST}?${queryString}`;
    return axiosInstance.get(endpoint);
};

export const getDrawInfoById = (drawId: string) => {
    return axiosInstance.get(`${DRAW_BY_ID}${drawId}`);
};

// Get current promo multiplier and optional extras for a base count
export const getCurrentPromo = (drawId: string, base?: number) => {
    const qs = base !== undefined ? `?base=${encodeURIComponent(String(base))}` : '';
    return axiosInstance.get(`${DRAW_PROMO}${drawId}/promo${qs}`);
};

export const getDrawStats = (drawId: string) => {
    return axiosInstance.get(`${DRAW_STATS}${drawId}/stats`);
};

export const uploadImage = (file: FormData) => {
    const payload = file;
    return axiosInstance.post(UPLOAD_IMAGE, payload);
};

export const getDrawMembeships = (drawId: string, membersFilter: DrawMembersFilter) => {
    const queryString = buildQueryString(membersFilter);
    const endpoint = `${DRAW_MEMBERSHIPS}${drawId}?${queryString}`;
    return axiosInstance.get(endpoint);
};

export const cancelDraw = (drawId: string) => {
    return axiosInstance.delete(`${DELETE_DRAW}${drawId}`);
};

export const updateDraw = async (drawId: string, payload: Partial<DrawFormData>) => {
    try {
        const response = await axiosInstance.put(`${UPDATE_DRAW}${drawId}`, payload);
        
        // Return response with any warnings - let calling code handle display
        return response;
    } catch (error) {
        console.error('Error updating draw:', error);
        throw error;
    }
};

// Create promotional draw helper
export const createPromotionalDraw = async (payload: any) => {
    try {
        const response = await axiosInstance.post(CREATE_PROMO_DRAW, payload);
        
        // Return response with any warnings - let calling code handle display
        return response;
    } catch (error) {
        console.error('Error creating promotional draw:', error);
        throw error;
    }
};

// Sign up for promotional draw (public route, no auth required)
export const signupPromotionalDraw = (payload: any) => axiosInstance.post('/public/club-owner/promo-draw-signup', payload);

// Get public draws list (no authentication required)
export const getPublicDrawsList = (drawFilters: Partial<DrawListFilter>) => {
    const queryString = buildQueryString(drawFilters);
    const endpoint = `/public/club-owner/draws?${queryString}`;
    return axiosInstance.get(endpoint);
};